import styles from "./PaymentSettings.module.scss";
import FormItemInput from "../../../components/ui/FormItemInput/FormItemInput";
import CustomButton from "../../../components/ui/CustomButton/CustomButton";
import { Form, FormInstance } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { StripeSettings } from "../../../models/StripeSettings";
import { useTranslation } from "react-i18next";
import { SettingsService } from "../../../services/settings.service";
import useAppDispatch from "../../../hooks/useAppDispatch";
import { openSuccessMessage } from "../../../store/message/messageSlice";
import { handleError } from "../../../helper/errorHandler";

const PaymentSettings = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [stripeSettings, setStripeSettings] = useState<StripeSettings | null>(null);
  const formRef = useRef<FormInstance>(null);

  useEffect(() => {
    getStripeSettings();
  }, []);

  useEffect(() => {
    if (stripeSettings && formRef.current) {
      formRef.current.setFieldsValue(stripeSettings);
    }
  }, [stripeSettings]);

  const getStripeSettings = async () => {
    try {
      const response = await SettingsService.getStripeService();
      if (response.data && response.status === 200) {
        setStripeSettings(response.data);
      }
    } catch (e) {}
  };

  const onFinish = async (values: Omit<StripeSettings, "id" | "apiVersion">) => {
    try {
      await SettingsService.updateStripeSettings(values);
      getStripeSettings();
      dispatch(openSuccessMessage(t("StripeSettingsUpdatedSuccessfully")));
    } catch (e) {
      handleError(e, dispatch, t);
    }
  };

  return (
    <div>
      <svg
        className={styles.svg}
        width="149"
        height="46"
        viewBox="0 0 149 46"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M137.93 24.0371C137.93 17.7774 134.909 12.838 129.134 12.838C123.336 12.838 119.827 17.7774 119.827 23.9883C119.827 31.3485 123.969 35.0651 129.914 35.0651C132.813 35.0651 135.006 34.4049 136.663 33.4757V28.5851C135.006 29.4166 133.106 29.9301 130.693 29.9301C128.33 29.9301 126.235 29.0987 125.967 26.2133H137.881C137.881 25.8954 137.93 24.6239 137.93 24.0371ZM125.894 21.7141C125.894 18.9509 127.575 17.8016 129.11 17.8016C130.596 17.8016 132.18 18.9509 132.18 21.7141H125.894ZM110.423 12.838C108.035 12.838 106.5 13.9627 105.647 14.7453L105.33 13.2293H99.9703V41.7407L106.061 40.4448L106.086 33.5248C106.963 34.1606 108.254 35.0653 110.398 35.0653C114.759 35.0653 118.731 31.5441 118.731 23.7928C118.706 16.7015 114.686 12.838 110.423 12.838ZM108.961 29.6856C107.523 29.6856 106.671 29.1721 106.086 28.5363L106.061 19.4644C106.695 18.7553 107.572 18.2663 108.961 18.2663C111.178 18.2663 112.713 20.7604 112.713 23.9637C112.713 27.2404 111.202 29.6856 108.961 29.6856ZM91.5891 11.3952L97.7045 10.0749V5.11108L91.5891 6.40704V11.3952ZM91.5891 13.2535H97.7045V34.6492H91.5891V13.2535ZM85.0352 15.063L84.6453 13.2535H79.3827V34.6492H85.4737V20.149C86.9112 18.2661 89.3476 18.6086 90.1029 18.8775V13.2535C89.3233 12.9602 86.4726 12.4223 85.0352 15.063ZM72.8531 7.94747L66.9082 9.21902L66.8839 28.8052C66.8839 32.4242 69.5882 35.0895 73.1942 35.0895C75.1921 35.0895 76.6538 34.7226 77.4579 34.2825V29.3187C76.6783 29.6366 72.8288 30.7613 72.8288 27.1425V18.462H77.4579V13.2537H72.8288L72.8531 7.94747ZM56.3829 19.4644C56.3829 18.5108 57.1625 18.1441 58.4538 18.1441C60.3055 18.1441 62.6444 18.7064 64.4962 19.7091V13.9627C62.474 13.1559 60.4761 12.838 58.4538 12.838C53.508 12.838 50.2188 15.4299 50.2188 19.7579C50.2188 26.5068 59.4772 25.4309 59.4772 28.3407C59.4772 29.4656 58.5027 29.8323 57.1382 29.8323C55.116 29.8323 52.5334 29.0009 50.4868 27.8761V33.6957C52.7527 34.6738 55.043 35.0895 57.1382 35.0895C62.2059 35.0895 65.69 32.5708 65.69 28.194C65.6657 20.9072 56.3829 22.2032 56.3829 19.4644Z"
          fill="#353535"
        />
        <rect y="11" width="24" height="24" rx="5" fill="#3B82F6" />
        <path
          d="M19.6282 17.3717C19.1332 16.876 18.3293 16.8763 17.8336 17.3717L9.75659 25.449L6.1667 21.8591C5.671 21.3634 4.86748 21.3634 4.37178 21.8591C3.87607 22.3548 3.87607 23.1584 4.37178 23.6541L8.85894 28.1412C9.10663 28.3889 9.43143 28.5131 9.75625 28.5131C10.0811 28.5131 10.4062 28.3892 10.6539 28.1412L19.6282 19.1666C20.1239 18.6712 20.1239 17.8673 19.6282 17.3717Z"
          fill="white"
        />
      </svg>
      <Form
        className={styles.form}
        ref={formRef}
        name="stripe-settings"
        onFinish={onFinish}
        autoComplete="off"
        layout={"vertical"}
      >
        <FormItemInput
          name={"publicKey"}
          label={t("PublicKey") + " *"}
          rules={{ required: true }}
        />

        <FormItemInput name={"secretKey"} label={t("Secret") + " *"} rules={{ required: true }} />

        <FormItemInput
          name={"webHookSecret"}
          label={t("WebhookSecret") + " *"}
          rules={{ required: true }}
        />

        <div className={styles.btnWrapper}>
          <CustomButton text={t("SaveChanges")} type={"submit"} typeStyle={"fill"} />
        </div>
      </Form>
    </div>
  );
};

export default PaymentSettings;
